import { fiveNumbers, fiveToNineNumbersMap } from '~/utils'

import { type CountWhoAndWhatResult } from './transformWhoAndWhatResult'
import {
  type ComparisonBrand,
  type NpiDriverAnswerData,
} from './useNpiDriverTableData'

/** compare.test.value の定義 */
const COMPARE_TEST_VALUE = {
  /** 判定不能 */
  UNDETERMINED: 0,
  /** 自社ブランド優位 */
  OWN_BRAND_ADVANTAGE: 1,
  /** 競合ブランド優位 */
  COMPETITOR_BRAND_ADVANTAGE: 2,
} as const

/**
 * API レスポンス (CountWhoAndWhatResult) を
 * テーブル表示用 NpiDriverAnswerData[] に変換
 *
 * 処理:
 * 1. perception + keyPoint の全回答を処理
 * 2. 各回答選択肢（option）ごとに集約
 * 3. npiRatio/importanceRatio を ByNineSegment（9seg）と ByFiveSegment（5seg母数加重集約）の両方で生成
 *
 * @param result - API レスポンス
 * @param brandIdToName - brandId（Int）→ ブランド名のマップ（compare結果の名前解決用）
 * @returns NpiDriverAnswerData[]
 */
export const transformToNpiDriverData = (
  result: CountWhoAndWhatResult | null | undefined,
  brandIdToName: ReadonlyMap<number, string> = new Map(),
): NpiDriverAnswerData[] => {
  if (!result) {
    return []
  }

  // option ごとにマッピング
  const dataByOption = new Map<
    number,
    {
      option: number
      text: string
      npiDistribution: Record<number, number>
      importanceDistribution: Record<number, number>
    }
  >()

  // option ごとに両側の text を Set に集め、2 種類以上あれば揺れとみなす。
  // 片側内で複数 text が混在するケースも検出できる。
  const textsByOption = new Map<number, Set<string>>()

  const collectText = (option: number, text: string) => {
    let texts = textsByOption.get(option)
    if (!texts) {
      texts = new Set<string>()
      textsByOption.set(option, texts)
    }
    texts.add(text)
  }

  // keyPoint（重視点設問）側を先に処理し、テキスト揺れがある場合の表示テキストはkeyPoint 側を採用する。
  for (const question of result.keyPoint) {
    for (const answer of question.answers) {
      const { option, text } = answer.answerOption
      collectText(option, text)

      if (!dataByOption.has(option)) {
        dataByOption.set(option, {
          option,
          text,
          npiDistribution: {},
          importanceDistribution: {},
        })
      }

      const item = dataByOption.get(option)!
      for (const dist of answer.distribution) {
        item.importanceDistribution[dist.segmentNumber] =
          (item.importanceDistribution[dist.segmentNumber] ?? 0) + dist.count
      }
    }
  }

  for (const question of result.perception) {
    for (const answer of question.answers) {
      const { option, text } = answer.answerOption
      collectText(option, text)

      if (!dataByOption.has(option)) {
        dataByOption.set(option, {
          option,
          text,
          npiDistribution: {},
          importanceDistribution: {},
        })
      }

      const item = dataByOption.get(option)!
      for (const dist of answer.distribution) {
        item.npiDistribution[dist.segmentNumber] =
          (item.npiDistribution[dist.segmentNumber] ?? 0) + dist.count
      }
    }
  }

  const segTotals = new Map<number, number>(
    result.distribution.map((d) => [d.segmentNumber, d.count]),
  )

  // PoD_score = norm(lift_SA) × norm(max(RSI, 0))。スコア値そのものをテーブルに表示する
  const podScoreByOption = new Map<number, number>(
    (result.riskPotential?.pod ?? []).map((p) => [p.option, p.score]),
  )

  // compare 結果から、指定した判定値（value）に一致する競合ブランド名を option ごとに集約する。
  // value===1（自社優位）→比較優位（Defensible）。
  const collectBrandsByOption = (
    targetValue: number,
  ): Map<number, ComparisonBrand[]> => {
    const byOption = new Map<number, ComparisonBrand[]>()
    for (const compareItem of result.riskPotential?.compare ?? []) {
      const brands = (compareItem.test ?? [])
        .filter((t) => t.value === targetValue)
        .sort((a, b) => a.brandId - b.brandId)
        .flatMap((t) => {
          const name = brandIdToName.get(t.brandId)

          return name !== undefined ? [{ id: t.brandId, name }] : []
        })
      if (brands.length > 0) {
        byOption.set(compareItem.option, brands)
      }
    }

    return byOption
  }

  const comparisonAdvantageBrandsByOption = collectBrandsByOption(
    COMPARE_TEST_VALUE.OWN_BRAND_ADVANTAGE,
  )

  // 9seg のカウント分布から、9seg 比率マップと 5seg 比率マップを生成する。
  // 5seg は構成する 2 つの 9seg のカウント和 ÷ 母数和で算出する（比率の単純平均ではない）。
  const toRatioMaps = (distribution: Record<number, number>) => {
    const byNineSegment: Record<number, number> = {}
    for (const [seg, count] of Object.entries(distribution)) {
      const segNum = Number(seg)
      const segTotal = segTotals.get(segNum) ?? 0
      byNineSegment[segNum] = segTotal > 0 ? count / segTotal : 0
    }

    const byFiveSegment: Record<number, number> = {}
    for (const fiveNumber of fiveNumbers) {
      const nineNumbers = fiveToNineNumbersMap[fiveNumber]
      const count = nineNumbers.reduce(
        (sum, n) => sum + (distribution[n] ?? 0),
        0,
      )
      const total = nineNumbers.reduce(
        (sum, n) => sum + (segTotals.get(n) ?? 0),
        0,
      )
      byFiveSegment[fiveNumber] = total > 0 ? count / total : 0
    }

    return { byFiveSegment, byNineSegment }
  }

  const items: NpiDriverAnswerData[] = []

  for (const data of dataByOption.values()) {
    const npi = toRatioMaps(data.npiDistribution)
    const importance = toRatioMaps(data.importanceDistribution)

    items.push({
      option: data.option,
      text: data.text,
      npiRatioByNineSegment: npi.byNineSegment,
      npiRatioByFiveSegment: npi.byFiveSegment,
      importanceRatioByNineSegment: importance.byNineSegment,
      importanceRatioByFiveSegment: importance.byFiveSegment,
      comparisonAdvantageBrands:
        comparisonAdvantageBrandsByOption.get(data.option) ?? [],
      podScore: podScoreByOption.get(data.option) ?? null,
      hasTextMismatch: (textsByOption.get(data.option)?.size ?? 0) > 1,
    })
  }

  return items.sort((a, b) => a.option - b.option)
}
